import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { SERVICE_MODELS } from '../data/mock';
import { Check, Clock, Zap, Calendar, Package, Truck, Recycle, Shield, Heart, Star, MapPin, ArrowRight } from 'lucide-react';

const iconMap = {
  standard: Clock,
  subscription: Calendar,
  priority: Zap,
};

const ServicesPage = () => {
  return (
    <main className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F8FAFC] to-white py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-[#111111]">
              Our Services
            </h1>
            <p className="mt-6 text-xl text-[#475569] leading-relaxed">
              Tailored logistics solutions designed specifically for battery installers. 
              We handle what you don't want to — so you can focus on what you do best.
            </p>
          </div>
        </div>
      </section>

      {/* Our Promise Section */}
      <section className="py-10 bg-[#0D9488]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-white text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              Our Promise
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              We'll look after your customers the way you would
            </h2>
            <p className="mt-6 text-lg text-white/90 leading-relaxed">
              Your reputation matters. When we interact with your customers, we represent you. 
              That's why we promise to deliver the same level of care, professionalism, and 
              attention to detail that you provide in every installation. Your customers become our customers.
            </p>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111]">
              What We Do
            </h2>
            <p className="mt-4 text-lg text-[#475569]">
              Two core services designed to improve your efficiency and keep you focused on installations
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Service 1: Packaging Collection & Disposal */}
            <Card className="border-[#E2E8F0] shadow-lg overflow-hidden">
              <div className="bg-gradient-to-br from-[#F0FDFA] to-[#E0F2FE] p-8">
                <div className="w-16 h-16 rounded-2xl bg-[#0D9488] flex items-center justify-center mb-6">
                  <Recycle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#111111]">
                  Packaging Collection & Disposal
                </h3>
                <p className="mt-3 text-[#475569]">
                  We collect, remove, and responsibly dispose of all installation packaging
                </p>
              </div>
              <CardContent className="p-8">
                <ul className="space-y-4">
                  {[
                    "Collect bulky packaging from your customer's site",
                    "Cardboard, foam, plastic wrap, and protective materials",
                    "Responsible sorting and recycling where possible",
                    "Proper disposal through appropriate facilities",
                    "Photo confirmation once collection is complete",
                    "Direct coordination with your customers"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#0D9488] flex-shrink-0 mt-0.5" />
                      <span className="text-[#475569]">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link to="/book/waste">
                    <Button className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white">
                      Book Collection
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Service 2: Pickup & Delivery */}
            <Card className="border-[#E2E8F0] shadow-lg overflow-hidden">
              <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] p-8">
                <div className="w-16 h-16 rounded-2xl bg-[#D97706] flex items-center justify-center mb-6">
                  <Truck className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#111111]">
                  Pickup & Delivery
                </h3>
                <p className="mt-3 text-[#475569]">
                  Equipment logistics to support your installation workflow
                </p>
              </div>
              <CardContent className="p-8">
                <ul className="space-y-4">
                  {[
                    "Pickup equipment from suppliers or warehouses",
                    "Deliver to job sites or customer locations",
                    "Flexible scheduling around your install calendar",
                    "Careful handling of valuable equipment",
                    "Real-time tracking and delivery confirmation",
                    "Same professional service your customers expect"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
                      <span className="text-[#475569]">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link to="/book/delivery">
                    <Button className="w-full bg-[#D97706] hover:bg-[#B45309] text-white">
                      Book Delivery
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Installers Choose Us */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-3xl font-bold text-[#111111]">
              Why Battery Installers Choose Us
            </h2>
            <p className="mt-4 text-lg text-[#475569]">
              We understand the unique challenges of battery installation work
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: Clock, 
                title: 'Save Time', 
                desc: 'No more tip runs or weekend disposal trips' 
              },
              { 
                icon: Truck, 
                title: 'Free Up Space', 
                desc: 'Keep your van clear for tools and equipment' 
              },
              { 
                icon: Star, 
                title: 'Professional Image', 
                desc: 'Clean site handover impresses customers' 
              },
              { 
                icon: Shield, 
                title: 'Reliable Partner', 
                desc: 'We show up when we say we will' 
              },
            ].map((item, index) => (
              <Card key={index} className="border-[#E2E8F0] text-center">
                <CardContent className="pt-8 pb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-[#0D9488]" />
                  </div>
                  <h3 className="font-semibold text-[#111111]">{item.title}</h3>
                  <p className="mt-2 text-sm text-[#64748B]">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Levels */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-3xl font-bold text-[#111111]">Service Levels</h2>
            <p className="mt-4 text-lg text-[#475569]">
              Choose the option that fits your workflow and volume
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {SERVICE_MODELS.map((model) => {
              const IconComponent = iconMap[model.id];
              return (
                <Card 
                  key={model.id} 
                  className={`card-hover ${
                    model.highlighted 
                      ? 'service-card-highlighted shadow-lg' 
                      : 'border-[#E2E8F0]'
                  }`}
                >
                  <CardHeader className="pt-8">
                    <div className="w-12 h-12 rounded-xl bg-[#F0FDFA] flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-[#0D9488]" />
                    </div>
                    <CardTitle className="text-xl font-bold text-[#111111]">
                      {model.name}
                    </CardTitle>
                    <CardDescription className="text-[#0D9488] font-medium">
                      {model.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {model.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-[#0D9488] flex-shrink-0 mt-0.5" />
                          <span className="text-[#475569]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link to="/pricing">
              <Button size="lg" className="bg-[#0D9488] hover:bg-[#0F766E] text-white px-8">
                View Pricing Options
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-10 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <MapPin className="w-8 h-8 text-[#0D9488] flex-shrink-0" />
              <div>
                <h3 className="text-2xl font-bold text-white">Service Area</h3>
                <p className="mt-2 text-[#94A3B8]">
                  Currently servicing Metro Melbourne LGAs only (Phase 1)
                </p>
              </div>
            </div>
            <Link to="/service-area">
              <Button variant="outline" className="border-[#374151] text-white hover:bg-[#1F2937]">
                Check your postcode
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#111111]">Ready to improve your efficiency?</h2>
          <p className="mt-4 text-lg text-[#475569]">
            Let us handle the logistics so you can focus on installations
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/book/waste">
              <Button size="lg" className="bg-[#0D9488] hover:bg-[#0F766E] text-white px-8">
                Book Now
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-[#E2E8F0] text-[#111111] hover:border-[#0D9488] px-8">
                Request Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
